import { useState } from 'react';
import { Box, Typography, Switch, TextField, Button, Divider } from '@mui/material';
import { useAuth } from '@clerk/clerk-react';
import siteConfigService from '../../services/SiteConfigService';
import { useSiteConfigContext } from '../../contexts/SiteConfigContext'; // ADDED
import _ from 'lodash';

const J = {
    surface: '#0d1f33',
    border: '0.5px solid #294766',
    text: '#e0f0ff',
    muted: '#a3d1ff',
    accent: '#66b2ff',
};

const keys = [
    { key: 'landingPageActive', label: 'Landing page active' },
    { key: 'signupActive', label: 'Signup active' },
    { key: 'loginActive', label: 'Login active' },
    { key: 'pricingActive', label: 'Pricing active' },
    { key: 'tierFreeActive', label: 'Tier Free active' },
    { key: 'tierStdActive', label: 'Tier Standard active' },
    { key: 'tierProActive', label: 'Tier Pro active' },
];

/**
 * Admin tab — site config management. Admin only.
 */
export default function AdminTab() {
    const { getToken } = useAuth();
    const siteConfig = useSiteConfigContext(); // ADDED

    const [form, setForm] = useState({
        version: siteConfig?.version ?? '',
        landingPageActive: siteConfig?.landingPageActive ?? true,
        signupActive: siteConfig?.signupActive ?? true,
        loginActive: siteConfig?.loginActive ?? true,
        pricingActive: siteConfig?.pricingActive ?? true,
        siteBanner: siteConfig?.siteBanner ?? '',
        siteDescription: siteConfig?.siteDescription ?? '',
        pricingWarning: siteConfig?.pricingWarning ?? '',
        loggedOnMsg: siteConfig?.loggedOnMsg ?? '',

        tierFreeActive: siteConfig?.tierFreeActive ?? true,
        tierStdActive: siteConfig?.tierStdActive ?? false,
        tierProActive: siteConfig?.tierProActive ?? false,
    });

    const handleToggle = (key) => {
        setForm((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleText = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        try {
            const token = await getToken();

            const dto = {
                data: form,
            };

            await siteConfigService.saveSiteConfig(dto, token);
        } catch (ex) {
            // Silent fail for now
        }
    };

    return (
        <Box id="admin-tab-root" display="flex" flexDirection="column" gap={3} width="100%">
            <Typography variant="body2" sx={{ color: J.muted }}>
                Site Configuration
            </Typography>

            {/* Toggles */}
            <Box id="admin-tab-toggles" sx={{ bgcolor: J.surface, border: J.border, borderRadius: 1, p: 2.5 }}>
                {_.orderBy(keys, ['label', 'desc']).map(({ key, label }) => (
                    <Box key={key} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" sx={{ color: J.text }}>
                            {label}
                        </Typography>
                        <Switch
                            checked={form[key]}
                            onChange={() => handleToggle(key)}
                            sx={{ '& .MuiSwitch-thumb': { bgcolor: form[key] ? J.accent : J.muted } }}
                        />
                    </Box>
                ))}
            </Box>

            <Divider sx={{ borderColor: '#294766' }} />

            {/* Text inputs */}
            <Box id="admin-tab-texts" display="flex" flexDirection="column" gap={2}>
                {[
                    { key: 'version', label: 'Version' },
                    { key: 'siteBanner', label: 'Site banner' },
                    { key: 'siteDescription', label: 'Site description' },
                    { key: 'pricingWarning', label: 'Pricing warning' },
                    { key: 'loggedOnMsg', label: 'Logged on message' },
                ].map(({ key, label }) => (
                    <TextField
                        key={key}
                        label={label}
                        value={form[key]}
                        onChange={(e) => handleText(key, e.target.value)}
                        fullWidth
                        multiline
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': { color: J.text, borderColor: J.border },
                            '& .MuiInputLabel-root': { color: J.muted },
                        }}
                    />
                ))}
            </Box>

            <Divider sx={{ borderColor: '#294766' }} />

            {/* Save */}
            <Button
                variant="contained"
                onClick={handleSave}
                sx={{ bgcolor: J.accent, color: '#0d0d1a', fontWeight: 700, textTransform: 'none', alignSelf: 'flex-start' }}
            >
                Save
            </Button>
        </Box>
    );
}
